import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchDepartments, fetchOperators, type Department, type Operator } from '../api/catalog'
import {
  fetchCommissionRoles,
  fetchCommissionPlanRates,
  createCommissionPlanRateVersion,
  fetchCommissionOperatorProfiles,
  fetchCommissionBrackets,
  resolveCommissionBracket,
  type CommissionRole,
  type CommissionPlanRate,
  type CommissionOperatorProfile,
  type CommissionBracket,
  type CommissionBracketResolution,
} from '../api/commissions'
import { fetchMe, type LoginResponse } from '../api/auth'
import { CommissionProposalsPanel } from '../components/CommissionProposalsPanel'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'
import {
  FormGrid,
  FormSection, Page,
  PageHeader,
  Tabs,
  Callout,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  LoadingState,
  EmptyState,
  StatusPill,
  Dialog,
  Button,
  TextField,
  Select,
  Checkbox,
  Icon,
  FilterBar,
} from '../components/ui'

/** TASK-0264 (F-010). Human-readable labels for `CommissionMeasure`'s closed vocabulary — mirrors
 * the backend's own literal wire values verbatim (see `commissions.ts`'s own doc comment). */
const MEASURE_LABELS: Record<string, string> = {
  AnnualConsumptionKwh: 'Consumo annuo (kWh)',
  PowerKw: 'Potenza (kW)',
  ContractCountInPeriod: 'N. contratti nel periodo',
}

function formatMeasure(measure: string): string {
  return MEASURE_LABELS[measure] ?? measure
}

/** TASK-0265 (F-011). Mirrors the backend's own closed vocabularies
 * (`CommissionBracketSegment`/`CommissionBracketCommodity`) verbatim, for the "Prova scaglione"
 * mini-form's two dropdowns — an unrecognised value is a 400 server-side (see `commissions.ts`'s own
 * `resolveCommissionBracket` doc comment), so this form only ever offers the two real values. */
const CommissionBracketSegmentValues = ['business', 'consumer'] as const
const CommissionBracketCommodityValues = ['energia', 'gas'] as const

function formatAmount(value: number): string {
  return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

/** Inclusive on both ends (backend convention — see `CommissionBracket`'s own doc comment): `null`
 * `toValue` means "no upper bound" (the legacy package's own real open-ended top bracket). */
function formatRange(fromValue: number, toValue: number | null): string {
  const from = fromValue.toLocaleString('it-IT')
  return toValue === null ? `da ${from}` : `${from} – ${toValue.toLocaleString('it-IT')}`
}

/**
 * TASK-0260 (F-006). Mirrors `PolicyNames.Admin`'s `RequireRole(...)` set
 * (`EqualCrmPolicies.cs`) exactly — the UI affordance only; the server is the actual boundary
 * (`CommissionPlanRatesController.CreateVersion`'s own `[Authorize(PolicyNames.Admin)]`,
 * criterion 4). Same page-local, duplicated-on-purpose shape as `CommissionsPage.tsx`'s own
 * `isEqualStaff`.
 */
function isAdmin(auth: LoginResponse | null): boolean {
  return auth !== null && ['Administrator', 'EqualAdmin'].includes(auth.role)
}

/**
 * TASK-0265 (F-011). Mirrors `PolicyNames.EqualStaff`'s `RequireRole(...)` set (`EqualCrmPolicies.cs`)
 * exactly — a verbatim copy of `CommissionsPage.tsx`'s own `isEqualStaff` (that page doesn't export
 * it; small page-local helper, not a shared component, same reasoning as its own doc comment). Gates
 * the "Prova scaglione" mini-form: `GET /api/commission-brackets/resolve` is `EqualStaff`-only
 * server-side (narrower than every other GET in this module) — a UI affordance only, never the
 * boundary itself.
 */
function isEqualStaff(auth: LoginResponse | null): boolean {
  return (
    auth !== null &&
    [
      'Administrator',
      'EqualAdmin',
      'EqualDirettoreGenerale',
      'EqualDirettoreCommerciale',
      'EqualAreaManager',
      'EqualOperatore',
      'EqualBackOffice',
    ].includes(auth.role)
  )
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatPercentage(value: number): string {
  return `${value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

/** Same redacted-value affordance as `CommissionsPage.tsx`'s `AmountCell` — a lock icon with a
 * tooltip wherever the backend omitted a field (staff without `CanSeeRealAmounts`). */
function Locked({ title }: { title: string }) {
  return (
    <span title={title}>
      <Icon name="lock" size={18} />
    </span>
  )
}

interface SelectedCell {
  departmentId: number
  roleId: number
  departmentName: string
  roleName: string
}

export function CommissionPlanRatesPage() {
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  useEffect(() => {
    fetchMe().then(setAuth).catch(() => setAuth(null))
  }, [])
  const canAdminister = isAdmin(auth)
  const canUseDiagnostic = isEqualStaff(auth)
  // F-070: il tab "Proposte" (ciclo di vita delle modifiche al listino) e' solo per l'Admin.
  const [tab, setTab] = useState<'listino' | 'proposte'>('listino')

  const [departments, setDepartments] = useState<Department[]>([])
  const [roles, setRoles] = useState<CommissionRole[]>([])
  const [asOf, setAsOf] = useState(todayIso())
  const [rates, setRates] = useState<CommissionPlanRate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
    fetchCommissionRoles().then(setRoles).catch(() => setRoles([]))
  }, [])

  const loadRates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchCommissionPlanRates({ asOf })
      setRates(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento del listino')
    } finally {
      setLoading(false)
    }
  }, [asOf])

  useEffect(() => {
    void loadRates()
  }, [loadRates])

  // REQ-044-002: departments come from Catalog's own endpoint (src/api/catalog.ts,
  // fetchDepartments -> GET /api/departments) — CommissionPlanRate.DepartmentId carries no
  // cross-module FK (ADR-011 §1), so this page is the join point, not the backend.
  const activeDepartments = useMemo(
    () => [...departments].filter(d => d.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [departments],
  )
  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [roles],
  )

  const rateFor = useCallback(
    (departmentId: number, roleId: number) =>
      rates.find(r => r.departmentId === departmentId && r.commissionRoleId === roleId),
    [rates],
  )

  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null)

  // TASK-0264 (F-010). Operators come from Catalog's own endpoint (fetchOperators) — like
  // CommissionPlanRate.DepartmentId above, CommissionOperatorProfile/CommissionBracket's OperatorId
  // carries no cross-module FK (ADR-011 §1, fact 3), so this page is the join point, not the backend.
  const [operators, setOperators] = useState<Operator[]>([])
  useEffect(() => {
    fetchOperators()
      .then(result => setOperators(result.items))
      .catch(() => setOperators([]))
  }, [])
  const sortedOperators = useMemo(
    () => [...operators].sort((a, b) => a.name.localeCompare(b.name)),
    [operators],
  )

  return (
    <Page>
      <PageHeader
        title="Listino provvigioni"
        icon="request_quote"
        tabs={
          canAdminister ? (
            <Tabs
              aria-label="Sezioni del listino"
              items={[
                { id: 'listino', label: 'Listino' },
                { id: 'proposte', label: 'Proposte' },
              ]}
              value={tab}
              onChange={id => setTab(id === 'proposte' ? 'proposte' : 'listino')}
            />
          ) : undefined
        }
      />

      <FilterBar>
        <TextField
          type="date"
          label="Valido al"
          value={asOf}
          onChange={e => setAsOf(e.target.value)}
        />
      </FilterBar>

      {canAdminister && tab === 'proposte' && (
        <CommissionProposalsPanel departments={departments} roles={sortedRoles} currentUserId={auth?.id ?? null} />
      )}
      {/* R3-29: le proposte viste dal ciclo di vita uniforme (stato e simulazione; avanzano dal pannello qui sopra). */}
      {canAdminister && tab === 'proposte' && <RuleLifecyclePanel family="commission-rates" />}

      {tab === 'listino' && (
        <>
      <Callout tone="info">
        Ogni cella della matrice mostra la percentuale del listino aziendale settore × ruolo in
        vigore alla data selezionata (D-01: questa tabella, non una percentuale personale, decide il
        compenso). Clicca su una cella per vedere lo storico delle versioni{canAdminister
          ? ' o creare una nuova versione'
          : ''}.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : activeDepartments.length === 0 || sortedRoles.length === 0 ? (
        <EmptyState icon="grid_off" title="Nessun settore o ruolo disponibile" />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Settore \ Ruolo</th>
              {sortedRoles.map(role => (
                <th key={role.id} className="text-center">
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeDepartments.map(dept => (
              <tr key={dept.id}>
                <td className="md-typescale-emphasized-body-medium">{dept.name}</td>
                {sortedRoles.map(role => {
                  const rate = rateFor(dept.id, role.id)
                  return (
                    <td
                      key={role.id}
                      className="text-center md-num"
                      role="button"
                      title="Storico versioni"
                      onClick={() =>
                        setSelectedCell({
                          departmentId: dept.id,
                          roleId: role.id,
                          departmentName: dept.name,
                          roleName: role.name,
                        })
                      }
                    >
                      {rate ? (
                        rate.percentage !== undefined ? (
                          formatPercentage(rate.percentage)
                        ) : (
                          <Locked title="Percentuale non visibile" />
                        )
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {/* D-02: read-only — the personal-percentage override (F-007/F-008) is out of scope here. */}
      <Card variant="outlined" padding="none">
        <CardHeader title="Ruoli provvigionali" />
        <DataTable variant="plain">
          <thead>
            <tr>
              <th>Ruolo</th>
              <th>Ordine</th>
              <th>Paga differenziale</th>
            </tr>
          </thead>
          <tbody>
            {sortedRoles.map(role => (
              <tr key={role.id}>
                <td>{role.name}</td>
                <td className="text-muted">{role.sortOrder}</td>
                <td>
                  {role.paysDifferential ? (
                    <StatusPill tone="info">Sì</StatusPill>
                  ) : (
                    <StatusPill tone="neutral">No</StatusPill>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </Card>

      {/* TASK-0264 (F-010, P-14 + P-19a — REQ-018-003). Read-only: no write path exists anywhere
          for CommissionOperatorProfile/CommissionBracket yet (GET only, criterion 6/7). */}
      <Card variant="outlined">
        <CardHeader title="Scaglioni" />
        <CardContent>
          <BracketsSection operators={sortedOperators} canUseDiagnostic={canUseDiagnostic} />
        </CardContent>
      </Card>

        </>
      )}

      {selectedCell && (
        <CellHistoryModal
          cell={selectedCell}
          canAdminister={canAdminister}
          onClose={() => setSelectedCell(null)}
          onVersionCreated={() => void loadRates()}
        />
      )}
    </Page>
  )
}

function CellHistoryModal({
  cell,
  canAdminister,
  onClose,
  onVersionCreated,
}: {
  cell: SelectedCell
  canAdminister: boolean
  onClose: () => void
  onVersionCreated: () => void
}) {
  const [history, setHistory] = useState<CommissionPlanRate[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const result = await fetchCommissionPlanRates({
        departmentId: cell.departmentId,
        roleId: cell.roleId,
      })
      setHistory(result)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Errore durante il caricamento dello storico')
    } finally {
      setLoading(false)
    }
  }, [cell.departmentId, cell.roleId])

  useEffect(() => {
    void load()
  }, [load])

  // criterion 2: the cell's current open-ended active version, if any — shown so the Admin knows
  // what will be closed by a new version (never a retroactive rewrite, only ValidTo moves).
  const currentVersion = history.find(r => r.validTo === null && r.status === 'active')

  const [percentage, setPercentage] = useState('')
  const [validFrom, setValidFrom] = useState(todayIso())
  const [changeReason, setChangeReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  async function handleCreateVersion(): Promise<void> {
    setSaveError('')
    const percentageValue = Number(percentage.replace(',', '.'))
    if (!percentage.trim() || Number.isNaN(percentageValue)) {
      setSaveError('Inserisci una percentuale valida (0–100).')
      return
    }
    if (!validFrom) {
      setSaveError('Seleziona la data di validità della nuova versione.')
      return
    }
    if (!changeReason.trim()) {
      setSaveError('La motivazione è obbligatoria.')
      return
    }

    setSaving(true)
    try {
      await createCommissionPlanRateVersion({
        departmentId: cell.departmentId,
        commissionRoleId: cell.roleId,
        percentage: percentageValue,
        validFrom,
        changeReason: changeReason.trim(),
      })
      setPercentage('')
      setChangeReason('')
      setValidFrom(todayIso())
      await load()
      onVersionCreated()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio della nuova versione')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      as="form"
      onSubmit={(e) => { e.preventDefault(); if (canAdminister) void handleCreateVersion() }}
      title={`${cell.departmentName} — ${cell.roleName}`}
      actions={
        <Button variant="text" dismiss onClick={onClose}>
          Chiudi
        </Button>
      }
    >
      {saveError && <Callout tone="error">{saveError}</Callout>}
      {canAdminister && (
        <FormSection title="Nuova versione">
          <TextField
            type="number"
            step="0.01"
            min="0"
            max="100"
            label="Percentuale (%) *"
            value={percentage}
            onChange={e => setPercentage(e.target.value)}
          />
          <TextField
            type="date"
            label="Valida dal *"
            min={todayIso()}
            value={validFrom}
            onChange={e => setValidFrom(e.target.value)}
            supportingText={`REQ-019-001: non retroattiva — non prima di oggi${
              currentVersion ? ` e dopo il ${currentVersion.validFrom}` : ''
            }.`}
          />
          <TextField
            className="md-span-full"
            multiline
            rows={2}
            label="Motivazione *"
            value={changeReason}
            onChange={e => setChangeReason(e.target.value)}
            placeholder="Es. rinnovo listino 2026, correzione refuso settore Energia"
          />
          <div className="md-span-full">
            <Button
              variant="tonal"
              size="sm"
              type="submit"
              disabled={saving}
            >
              {saving ? 'Salvataggio...' : 'Crea nuova versione'}
            </Button>
          </div>
          {currentVersion && (
            <p className="md-span-full m-0 text-muted md-typescale-body-small">
              Chiude la versione attuale (valida dal {currentVersion.validFrom}, mai
              cancellata — resta consultabile nello storico) alla data scelta sopra.
            </p>
          )}
        </FormSection>
      )}

      <FormSection title="Storico versioni" columns={1}>
        {loadError && <Callout tone="error">{loadError}</Callout>}
        {loading ? (
          <LoadingState />
        ) : history.length === 0 ? (
          <div className="text-muted md-typescale-body-small py-2">
            Nessuna versione registrata per questa cella.
          </div>
        ) : (
          <div className="md-dialog__bleed">
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Valida dal</th>
                <th>Valida fino a</th>
                <th className="md-num">%</th>
                <th>Ver.</th>
                <th>Stato</th>
                <th>Motivazione</th>
                <th>Utente</th>
              </tr>
            </thead>
            <tbody>
              {history.map(r => (
                <tr key={r.id}>
                  <td>{r.validFrom}</td>
                  <td>
                    {r.validTo ?? <span className="text-muted">in corso</span>}
                  </td>
                  <td className="md-num">
                    {r.percentage !== undefined ? (
                      formatPercentage(r.percentage)
                    ) : (
                      <Locked title="Percentuale non visibile" />
                    )}
                  </td>
                  <td>{r.version}</td>
                  <td>
                    <StatusPill tone={r.status === 'active' ? 'success' : 'neutral'}>
                      {r.status}
                    </StatusPill>
                  </td>
                  <td>
                    {r.changeReason !== undefined ? (
                      r.changeReason ?? <span className="text-muted">—</span>
                    ) : (
                      <Locked title="Motivazione non visibile" />
                    )}
                  </td>
                  <td>
                    {r.createdBy !== undefined ? (
                      r.createdBy !== null ? (
                        `#${r.createdBy}`
                      ) : (
                        <span className="text-muted">—</span>
                      )
                    ) : (
                      <Locked title="Utente non visibile" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          </div>
        )}
      </FormSection>
    </Dialog>
  )
}

/**
 * TASK-0264 (F-010). The "Scaglioni" read-only card: an operator selector plus that operator's own
 * declared profile (`CommissionOperatorProfile`) and its bracket table (`CommissionBracket`). No
 * write path exists anywhere yet — this section only ever calls the two GET endpoints.
 */
function BracketsSection({
  operators,
  canUseDiagnostic,
}: {
  operators: Operator[]
  canUseDiagnostic: boolean
}) {
  const [operatorId, setOperatorId] = useState<number | ''>('')
  const [profile, setProfile] = useState<CommissionOperatorProfile | null>(null)
  const [brackets, setBrackets] = useState<CommissionBracket[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (operatorId === '') {
      setProfile(null)
      setBrackets([])
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      fetchCommissionOperatorProfiles({ operatorId }),
      fetchCommissionBrackets({ operatorId }),
    ])
      .then(([profiles, bracketRows]) => {
        if (cancelled) return
        setProfile(profiles[0] ?? null)
        setBrackets(bracketRows)
      })
      .catch(e => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Errore durante il caricamento degli scaglioni')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [operatorId])

  return (
    <div>
      <div className="mb-3">
        <Select
          label="Operatore"
          value={operatorId}
          onChange={e => setOperatorId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Seleziona un operatore…</option>
          {operators.map(op => (
            <option key={op.id} value={op.id}>
              {op.name}
            </option>
          ))}
        </Select>
      </div>

      {error && <Callout tone="error">{error}</Callout>}

      {operatorId === '' ? (
        <div className="text-muted md-typescale-body-small py-2">
          Seleziona un operatore per vedere il suo profilo e i suoi scaglioni.
        </div>
      ) : loading ? (
        <LoadingState />
      ) : (
        <>
          <div className="mb-3">
            <h3 className="md-typescale-title-small mb-2">Profilo operatore</h3>
            {profile ? (
              <div className="md-typescale-body-small">
                <div>
                  Misura dichiarata: <strong>{formatMeasure(profile.bracketMeasure)}</strong>
                </div>
                <div>
                  Extra email:{' '}
                  {profile.extraEmail !== undefined ? (
                    formatAmount(profile.extraEmail)
                  ) : (
                    <Locked title="Extra email non visibile" />
                  )}
                </div>
                <div>
                  Extra RID:{' '}
                  {profile.extraRid !== undefined ? (
                    formatAmount(profile.extraRid)
                  ) : (
                    <Locked title="Extra RID non visibile" />
                  )}
                </div>
              </div>
            ) : (
              <div className="text-muted md-typescale-body-small">Nessun profilo configurato per questo operatore.</div>
            )}
          </div>

          <h3 className="md-typescale-title-small mb-2">Scaglioni</h3>
          {brackets.length === 0 ? (
            <div className="text-muted md-typescale-body-small py-2">
              Nessuno scaglione configurato per questo operatore.
            </div>
          ) : (
            <DataTable>
              <thead>
                <tr>
                  <th>Segmento</th>
                  <th>Commodity</th>
                  <th>Misura</th>
                  <th>Intervallo</th>
                  <th className="md-num">Importo</th>
                  <th>Valido dal</th>
                  <th>Valido fino a</th>
                  <th>Ver.</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {brackets.map(b => (
                  <tr key={b.id}>
                    <td>{b.segment}</td>
                    <td>{b.commodity}</td>
                    <td>{formatMeasure(b.measure)}</td>
                    <td>{formatRange(b.fromValue, b.toValue)}</td>
                    <td className="md-num">
                      {b.amount !== undefined ? (
                        formatAmount(b.amount)
                      ) : (
                        <Locked title="Importo non visibile" />
                      )}
                    </td>
                    <td>{b.validFrom}</td>
                    <td>{b.validTo ?? <span className="text-muted">in corso</span>}</td>
                    <td>{b.version}</td>
                    <td>
                      <StatusPill tone={b.status === 'active' ? 'success' : 'neutral'}>
                        {b.status}
                      </StatusPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}

          {canUseDiagnostic && typeof operatorId === 'number' && (
            <ResolveBracketForm operatorId={operatorId} />
          )}
        </>
      )}
    </div>
  )
}

/**
 * TASK-0265 (F-011, P-19b — REQ-018-003). "Prova scaglione": a diagnostic mini-form backing
 * `GET /api/commission-brackets/resolve`. `EqualStaff`-gated server-side (narrower than every other
 * GET in this module) — rendered only when `canUseDiagnostic` (a UI affordance only, never the
 * boundary itself: a non-staff caller reaching this endpoint gets a 403 the `api()` helper turns
 * into a thrown `Error`, surfaced the same way as any other failed call below).
 */
function ResolveBracketForm({ operatorId }: { operatorId: number }) {
  const [segment, setSegment] = useState<string>(CommissionBracketSegmentValues[0])
  const [commodity, setCommodity] = useState<string>(CommissionBracketCommodityValues[0])
  const [measure, setMeasure] = useState<string>(Object.keys(MEASURE_LABELS)[0])
  const [value, setValue] = useState('')
  const [date, setDate] = useState(todayIso())
  const [hasEmailExtra, setHasEmailExtra] = useState(false)
  const [hasRidExtra, setHasRidExtra] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<CommissionBracketResolution | null>(null)

  // TASK-0265: a fresh operator selection invalidates any previous result — never show a stale
  // resolution for an operator the caller has since navigated away from.
  useEffect(() => {
    setResult(null)
    setError('')
  }, [operatorId])

  async function handleResolve(): Promise<void> {
    setError('')
    setResult(null)
    if (!value.trim()) {
      setError('Inserisci un valore da risolvere (es. consumo annuo in kWh).')
      return
    }
    if (!date) {
      setError('Seleziona una data.')
      return
    }

    setLoading(true)
    try {
      const resolved = await resolveCommissionBracket({
        operatorId,
        segment,
        commodity,
        measure,
        value: value.trim(),
        date,
        hasEmailExtra,
        hasRidExtra,
      })
      setResult(resolved)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante la risoluzione dello scaglione')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card variant="outlined" className="mt-4">
      <CardHeader title="Prova scaglione" />
      <CardContent>
        <FormGrid columns={4} className="md-form-grid--end">
          <div>
            <Select label="Segmento" value={segment} onChange={e => setSegment(e.target.value)}>
              {CommissionBracketSegmentValues.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Select label="Commodity" value={commodity} onChange={e => setCommodity(e.target.value)}>
              {CommissionBracketCommodityValues.map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Select label="Misura" value={measure} onChange={e => setMeasure(e.target.value)}>
              {Object.keys(MEASURE_LABELS).map(m => (
                <option key={m} value={m}>
                  {formatMeasure(m)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <TextField
              type="text"
              inputMode="decimal"
              label="Valore"
              placeholder="es. 4999.5"
              value={value}
              onChange={e => setValue(e.target.value)}
            />
          </div>
          <div>
            <TextField
              type="date"
              label="Data"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>
        </FormGrid>
        <div className="d-flex gap-3 align-items-center mt-2">
          <Checkbox
            id="resolve-has-email-extra"
            label="Extra email"
            checked={hasEmailExtra}
            onChange={e => setHasEmailExtra(e.target.checked)}
          />
          <Checkbox
            id="resolve-has-rid-extra"
            label="Extra RID"
            checked={hasRidExtra}
            onChange={e => setHasRidExtra(e.target.checked)}
          />
          <Button
            variant="tonal"
            size="sm"
            className="ms-auto"
            disabled={loading}
            onClick={() => void handleResolve()}
          >
            {loading ? 'Verifica...' : 'Verifica'}
          </Button>
        </div>

        {error && <Callout tone="error" className="mt-3">{error}</Callout>}

        {result && !error && (
          <div className="mt-3">
            {result.outcome === 'matched' && (
              <Callout tone="success">
                Scaglione trovato (intervallo {formatRange(result.fromValue ?? 0, result.toValue ?? null)}).{' '}
                Importo:{' '}
                {result.amount !== undefined ? (
                  <strong>{formatAmount(result.amount)}</strong>
                ) : (
                  <Locked title="Importo non visibile" />
                )}
                {result.bracketAmount !== undefined && (
                  <span className="text-muted"> (di cui {formatAmount(result.bracketAmount)} di scaglione)</span>
                )}
                {/* TASK-0265 correction: which extras applied is reported explicitly by the backend
                    (`extrasApplied`), never deduced by comparing bracketAmount to amount — that
                    comparison is ambiguous exactly when a requested extra is configured as zero
                    (applied, but no visible difference in the total). */}
                {result.extrasApplied && (result.extrasApplied.email || result.extrasApplied.rid) && (
                  <div className="mt-1 text-muted">
                    Extra applicati:{' '}
                    {[
                      result.extrasApplied.email ? 'email' : null,
                      result.extrasApplied.rid ? 'RID' : null,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </Callout>
            )}
            {result.outcome === 'no_match' && (
              <Callout tone="warning">
                Nessuno scaglione corrisponde ai criteri indicati per questo operatore (fuori
                intervallo o nessuno scaglione configurato — mai un importo pari a zero).
              </Callout>
            )}
            {result.outcome === 'incoherent' && (
              <Callout tone="error">
                Dati incoerenti: più scaglioni attivi coprono lo stesso valore.
                {result.error && <div className="mt-1 text-break">{result.error}</div>}
              </Callout>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
